/**
 * ============================================================================
 * ⭐ ZPL: THE PURE PART. NO REACT, NO IMPORTS, NO BROWSER, NO NODE.
 * ============================================================================
 *
 * ⛔⛔ THIS FILE EXISTS TWICE IN THIS REPO AND MUST BE BYTE-IDENTICAL:
 *
 *     src/lib/zpl.ts        — the Function App (printContent's guard)
 *     web/src/lib/zpl.ts    — the browser (useZebraPrint's guard)
 *
 * ⚠️ AND A THIRD COPY LIVES IN PIPELINE'S laborclock, which is an HTML app —
 * it cannot import a hook or a module from here, but it CAN take this file
 * verbatim. That is why there are no imports: an import is what would make the
 * copy impossible.
 *
 * ⛔ THE DUPLICATION IS DELIBERATE, AND IT IS POLICED BY A TEST.
 * `tools/zebra-print-test.cjs` asserts the two in-repo copies are identical
 * byte for byte. ⚠️ WITHOUT THAT TEST THIS IS THE CLASSIC DRIFT BUG: the
 * server would accept a label the browser refuses, or vice versa, and the
 * disagreement would surface as "printing works on my machine".
 *
 * ⭐ WHY THE SERVER NEEDS ITS OWN COPY AT ALL: the browser check runs on the
 * RENDERED result; the server check runs on an UPLOADED FILE the browser never
 * looked at. Neither implies the other, and both use the same three rules.
 *
 * ⭐ It also makes this testable. A hook needs a renderer and a DOM; these are
 * three string functions, and `tools/zebra-print-test.cjs` pins them.
 * ============================================================================
 */

/**
 * ---- ⛔⛔ A SUBSTITUTED VALUE IS AN INJECTION, AND IT FAILS SILENTLY -------
 *
 * ⚠️ `^` AND `~` ARE ZPL'S TWO COMMAND PREFIXES. A PO number, customer name or
 * reference containing one does not print as text — **it starts a command.**
 *
 * ⛔ A value holding `^XZ` ENDS THE LABEL EARLY: the printer emits a short label
 * with half its content missing and reports complete success. Nothing errors,
 * nothing logs, and the only evidence is a wrong label somebody has already
 * stuck on a pallet.
 *
 * ⚠️ REMOVED, NOT ESCAPED. ZPL has no escape for these outside a field block,
 * and a mangled command is worse than a missing character. ⭐ Replaced with a
 * SPACE rather than deleted, so `A^B` reads as `A B` rather than silently
 * becoming `AB` — a visibly odd value beats a plausible wrong one.
 */
export function zplSafe(value: unknown): string {
    return String(value ?? '').replace(/[\^~]/g, ' ');
}

/**
 * ---- ⭐ `{{name}}` SUBSTITUTION, DELIBERATELY THE DUMBEST THING THAT WORKS -
 *
 * The placeholder vocabulary is documented per template in
 * `dbo.PrintTemplate.PlaceholderHelp` — prose, for whoever edits the ZPL.
 *
 * ⛔ AN UNKNOWN PLACEHOLDER IS LEFT AS-IS, NOT BLANKED. `{{loadNumber}}`
 * printed literally on a label is a visible bug somebody reports within an
 * hour; an empty gap where a load number should be is one nobody notices until
 * the wrong pallet ships.
 *
 * ⚠️ A key present with an empty value IS substituted — that is a real answer
 * ("this order has no PO"), not a missing one.
 */
export function renderZpl(template: string, values: Record<string, unknown>): string {
    return template.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (whole, key: string) =>
        Object.prototype.hasOwnProperty.call(values, key) ? zplSafe(values[key]) : whole);
}

/**
 * ---- ⛔⛔ INCOMPLETE ZPL PRINTS NOTHING AND REPORTS NOTHING ---------------
 *
 * ⚠️ A label must open with `^XA` and close with `^XZ`. Without the closing
 * command **the printer waits for the rest of a command that never arrives** —
 * which is indistinguishable from a dead printer, and is the single most
 * confusing failure in the whole feature.
 *
 * ⭐ `CK_PrintTemplate_Zpl` guards the STORED template. This guards the
 * RENDERED result and the UPLOADED FILE, neither of which the database ever
 * sees — a template can be valid and a substitution can still produce
 * something that is not.
 */
export function isCompleteZpl(zpl: string): boolean {
    return zpl.includes('^XA') && zpl.includes('^XZ');
}
